import {
  Col, Row, Table, InputNumber, Typography, Space, Alert, ConfigProvider, Tooltip
} from 'antd';
import { Button } from '../../../../../design-system';
import { ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { useMemo } from 'react';
import { Chart } from '../../../../chart/BaseChart';

import { Accordion } from '../../../../../design-system/accordion';
import { useSwnKrwRegression } from '../hooks/useSwnKrwRegression';
import { RawDataPoint } from '../../logic';

const { Title, Text } = Typography;

const formatNumber = (val?: number) => (typeof val === 'number' ? val.toFixed(5) : '-');

const COLUMNS_TABLE = [
  {
    title: 'Swn (Calculated)',
    dataIndex: 'so',
    key: 'so',
    render: (val?: number) => formatNumber(val),
  },
  {
    title: 'Krw (Raw)',
    dataIndex: 'kro',
    key: 'kro',
    render: (val?: number) => formatNumber(val),
  },
];

export const SwnKrwRegressionAnalysis = () => {
  const {
    sample,
    sor,
    loading,
    error,
    rawDataTable,
    selectedRowKeys,
    setSelectedRowKeys,
    regressionResult,
    chartOptions,
    handleReset,
    handleSave,
    colors,
    isDay
  } = useSwnKrwRegression();

  const tableTheme = useMemo(() => ({
    components: {
      Table: {
        colorBgContainer: colors.bg,
        headerBg: colors.bg,
        headerColor: colors.text,
        colorText: colors.text,
        borderColor: colors.border,
        rowSelectedBg: colors.optionSelect, 
        rowSelectedHoverBg: isDay ? colors.optionSelect : '#434343',
        rowHoverBg: isDay ? 'rgba(0, 0, 0, 0.04)' : '#1f1f1f',
      },
      Pagination: {
        colorBgContainer: 'transparent',
        itemBg: 'transparent',
        itemActiveBg: colors.optionSelect,
        colorText: colors.text,
        colorPrimary: colors.colorPrimary,
        itemLinkBg: 'transparent',
        colorTextDisabled: colors.textSecondary
      }
    },
    token: {
        colorText: colors.text,
        colorBgContainer: colors.bg,
    }
  }), [colors, isDay]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => setSelectedRowKeys(newSelectedRowKeys),
    getCheckboxProps: (record: RawDataPoint) => ({ disabled: loading }),
  };

  return (
    <Accordion defaultOpen="swn-krw-regression">
      <Accordion.Item
        id="swn-krw-regression"
        header={
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontWeight: 'bold' }}>
            <span>Find True Krw</span>
            <span style={{ color: colors.waterOil }}>Water-Oil</span>
          </div>
        }
      >
        {sample ? (
          <div style={{ padding: '0.5rem' }}>
            <Row gutter={16}>
              <Col span={12}>
                <Title level={5}>1. Select Points and Parameters</Title>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space align="end">
                    <div>
                        <Text>True Sor:</Text>
                        <InputNumber
                          style={{ width: 100, display: 'block' }}
                          value={sor}
                          disabled={true}
                          readOnly={true}
                        />
                    </div>

                    <div>
                        <Text>True Krw (a):</Text>
                        <InputNumber
                          style={{ width: 120, display: 'block' }}
                          value={regressionResult?.a_max_calc} 
                          precision={5}
                          disabled={true}
                          readOnly={true}
                          placeholder="-"
                        />
                    </div>                    
                  </Space>

                  <Button
                    variant="primary"
                    startIcon={<SaveOutlined />}
                    onClick={handleSave}
                    loading={loading}
                    disabled={!regressionResult}
                  >
                    Save Regression Selection
                  </Button>
                  <Button
                    variant="secondary"
                    startIcon={<ReloadOutlined />}
                    onClick={handleReset}
                    disabled={loading}
                  >
                    Reset
                  </Button>

                  <ConfigProvider theme={tableTheme}>
                    <Table
                      loading={loading}
                      rowSelection={rowSelection}
                      columns={COLUMNS_TABLE}
                      dataSource={rawDataTable}
                      bordered
                      size="small"
                      pagination={{ pageSize: 20 }}
                      scroll={{ y: 300 }}
                      title={() => `Lab Points (Selected ${selectedRowKeys.length} of ${rawDataTable.length})`}
                    />
                  </ConfigProvider>
                </Space>
              </Col>

               <Col span={12}>
                <Title level={5}>2. Regression Results</Title>
                {error && <Alert message={error} type="error" showIcon />}

                {regressionResult && (
                  <Row style={{ marginBottom: '8px' }}>
                    <Col span={8}><Text strong>R²:</Text> {regressionResult.rSquared.toFixed(5)}</Col>
                    <Col span={8}><Text strong>a (Max_calc):</Text> {regressionResult.a_max_calc.toFixed(5)}</Col>
                    <Col span={8}><Text strong>b (Exponent):</Text> {regressionResult.b_exponent.toFixed(5)}</Col>
                  </Row>
                )}

                <div style={{ height: 400 }}>
                  <Chart
                    spec={chartOptions}
                    containerProps={{ style: { height: '100%' } }}
                  />
                </div>
              </Col>
            </Row>
          </div>
        ) : (
          <div className="no-data-div">No lab data to analyze. Save the lab curves in the Laboratory Data section first.</div>
        )}
      </Accordion.Item>
    </Accordion>
  );
};
